/**
 * audio/music.js — the procedural carnival waltz generator.
 *
 * No music files either. A track is a seeded 8-bar phrase over a fixed chord
 * progression, played oom-pah-pah: tuba on beat one, organ stabs on two and
 * three, a lead line noodling over the top with the chromatic passing notes
 * that make a tune sound like a fairground rather than a hymn.
 *
 * The phrase is generated once per track from a seed derived from its id, so a
 * track sounds the same every time you hear it — it is a tune, not endless
 * improvisation — while four tracks stay genuinely distinct.
 *
 * Scheduling is one bar at a time against a 2 s lookahead, driven by the shared
 * audio scheduler in context.js. There is no per-note timer.
 *
 * @module audio/music
 */

import {
  osc, noiseBurst, adsr, out, filter, makeRng, hashSeed, mtof,
} from './synth.js';
import {
  busNode, context, onTick, whenUnlocked, logPlay, LOG_ONLY, reportError,
} from './context.js';

/** How far ahead of the clock notes are scheduled. */
const LOOKAHEAD = 2.0;

/** @type {Record<string, object>} */
const TRACKS = {
  'hub-day': { bpm: 140, meter: 3, key: 60, style: 'waltz', bright: 1.0, gain: 1.0 },
  'hub-evening': { bpm: 122, meter: 3, key: 57, style: 'waltz', bright: 0.62, gain: 0.9 },
  'space-calm': { bpm: 74, meter: 4, key: 65, style: 'lullaby', bright: 0.55, gain: 0.85 },
  'victory': { bpm: 152, meter: 3, key: 60, style: 'waltz', bright: 1.5, gain: 1.15, bars: 4, loop: false },
};

/** Track aliases, including the id the space agent sends. */
const TRACK_ALIASES = Object.freeze({
  hub: 'hub-day',
  day: 'hub-day',
  evening: 'hub-evening',
  night: 'hub-evening',
  'space-music': 'space-calm',
  space: 'space-calm',
  'personal-space': 'space-calm',
  calm: 'space-calm',
  lullaby: 'space-calm',
  win: 'victory',
  jingle: 'victory',
});

/** I – I – IV – V7 – I – vi – ii – V7. The fairground standard. */
const PROGRESSION = [
  { r: 0, q: 'maj' }, { r: 0, q: 'maj' }, { r: 5, q: 'maj' }, { r: 7, q: 'dom7' },
  { r: 0, q: 'maj' }, { r: 9, q: 'min' }, { r: 2, q: 'min' }, { r: 7, q: 'dom7' },
];

const QUALITIES = Object.freeze({
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dom7: [0, 4, 7, 10],
});

const state = {
  /** @type {object|null} */
  current: null,
  /** @type {string|null} */
  pending: null,
  /** Track to return to when a non-looping jingle finishes. */
  resumeId: null,
  masterOut: null,
  distantTap: null,
  offTick: null,
  installed: false,
};

// ─────────────────────────────────────────────────────────────────────────────
// Phrase generation
// ─────────────────────────────────────────────────────────────────────────────

/** @returns {number[]} Absolute MIDI notes of a chord. */
function chordNotes(key, chord, octave = 0) {
  return QUALITIES[chord.q].map((i) => key + chord.r + i + octave * 12);
}

/**
 * Build the melody. Deterministic given the seed, which is what makes a track
 * recognisable rather than a different tune every loop.
 */
function buildPhrase(rng, key, meter) {
  return PROGRESSION.map((chord) => {
    const tones = chordNotes(key, chord, 1);
    const count = 2 + Math.floor(rng() * 3);
    const notes = [];
    for (let i = 0; i < count; i++) {
      let n = tones[Math.floor(rng() * tones.length)];
      // Chromatic approach notes — the carnival flavour.
      if (rng() < 0.26) n += rng() < 0.5 ? -1 : 1;
      if (rng() < 0.22) n += 12;
      notes.push({
        beat: (i / count) * meter + (rng() < 0.2 ? 0.25 : 0),
        note: n,
        dur: (meter / count) * 0.85,
      });
    }
    return notes;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Instruments
// ─────────────────────────────────────────────────────────────────────────────

/** Tuba — the "oom". Low triangle with a soft attack. */
function tuba(ctx, dest, note, t0, dur, gain) {
  osc(ctx, dest, {
    type: 'triangle', freq: mtof(note), t0,
    gain: 0.20 * gain, attack: 0.012, hold: dur * 0.2, decay: dur * 0.75,
  });
  osc(ctx, dest, {
    type: 'sine', freq: mtof(note) * 0.5, t0,
    gain: 0.10 * gain, attack: 0.014, decay: dur * 0.8,
  });
}

/** Organ stab — the "pah". Detuned squares through a lowpass. */
function stab(ctx, dest, notes, t0, dur, gain, bright) {
  const g = out(ctx, dest, 0);
  const lp = filter(ctx, 'lowpass', Math.max(600, 1500 * bright), 0.9);
  lp.connect(g);
  for (const n of notes) {
    for (const det of [-5, 4]) {
      const o = ctx.createOscillator();
      o.type = 'square';
      o.detune.value = det;
      o.frequency.value = mtof(n);
      const og = ctx.createGain();
      og.gain.value = 0.22;
      o.connect(og);
      og.connect(lp);
      o.start(t0);
      o.stop(t0 + dur + 0.06);
    }
  }
  adsr(g.gain, t0, { peak: 0.10 * gain, attack: 0.008, hold: dur * 0.25, decay: dur * 0.7 });
}

/** Lead — a reedy triangle with a little bite on the attack. */
function lead(ctx, dest, note, t0, dur, gain, bright) {
  osc(ctx, dest, {
    type: 'triangle', freq: mtof(note), t0,
    gain: 0.13 * gain, attack: 0.01, hold: dur * 0.3, decay: dur * 0.7, pan: 0.12,
  });
  if (bright > 0.8) {
    osc(ctx, dest, {
      type: 'square', freq: mtof(note), t0,
      gain: 0.035 * gain * bright, attack: 0.008, decay: dur * 0.4, pan: 0.12,
    });
  }
}

/** Lullaby pad — long, soft, no transient at all. */
function pad(ctx, dest, notes, t0, dur, gain) {
  const g = out(ctx, dest, 0);
  const lp = filter(ctx, 'lowpass', 900, 0.7);
  lp.connect(g);
  for (const n of notes) {
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = mtof(n);
    const og = ctx.createGain();
    og.gain.value = 0.3;
    o.connect(og);
    og.connect(lp);
    o.start(t0);
    o.stop(t0 + dur + 0.4);
  }
  adsr(g.gain, t0, { peak: 0.09 * gain, attack: dur * 0.35, hold: dur * 0.2, decay: dur * 0.6 });
}

// ─────────────────────────────────────────────────────────────────────────────
// Bar scheduling
// ─────────────────────────────────────────────────────────────────────────────

/** @private Schedule one bar of a waltz. */
function scheduleWaltz(ctx, dest, trk, barIndex, t0) {
  const { cfg, phrase, key } = trk;
  const beat = 60 / cfg.bpm;
  const chord = PROGRESSION[barIndex % PROGRESSION.length];
  const g = cfg.gain;

  tuba(ctx, dest, key + chord.r - 24, t0, beat * 0.9, g);
  const triad = chordNotes(key, chord, 0);
  stab(ctx, dest, triad, t0 + beat, beat * 0.55, g, cfg.bright);
  stab(ctx, dest, triad, t0 + beat * 2, beat * 0.55, g, cfg.bright);

  for (const n of phrase[barIndex % phrase.length]) {
    lead(ctx, dest, n.note, t0 + n.beat * beat, n.dur * beat, g, cfg.bright);
  }

  // A brushed tick on the offbeats keeps the pulse legible at low volume.
  if (cfg.bright > 0.8) {
    for (const b of [1, 2]) {
      noiseBurst(ctx, dest, {
        t0: t0 + beat * b, gain: 0.022 * g, attack: 0.002, decay: 0.05,
        type: 'highpass', freq: 4200,
      });
    }
  }
}

/** @private Schedule one bar of the 4/4 personal-space lullaby. */
function scheduleLullaby(ctx, dest, trk, barIndex, t0) {
  const { cfg, phrase, key } = trk;
  const beat = 60 / cfg.bpm;
  const chord = PROGRESSION[barIndex % PROGRESSION.length];
  const g = cfg.gain;

  pad(ctx, dest, chordNotes(key, chord, 0), t0, beat * cfg.meter, g);
  const tones = chordNotes(key, chord, 1);
  for (let i = 0; i < cfg.meter; i++) {
    osc(ctx, dest, {
      type: 'sine', freq: mtof(tones[i % tones.length]), t0: t0 + i * beat,
      gain: 0.07 * g, attack: 0.02, decay: beat * 0.8,
      pan: i % 2 ? 0.18 : -0.18,
    });
  }
  const bar = phrase[barIndex % phrase.length];
  if (bar.length) {
    const n = bar[0];
    osc(ctx, dest, {
      type: 'triangle', freq: mtof(n.note), t0: t0 + beat * 0.5,
      gain: 0.06 * g, attack: 0.04, decay: beat * 1.4,
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Transport
// ─────────────────────────────────────────────────────────────────────────────

/** @param {string} id @returns {string|null} */
export function resolveTrack(id) {
  if (!id) return null;
  const n = String(id).toLowerCase();
  if (TRACKS[n]) return n;
  const a = TRACK_ALIASES[n];
  return a && TRACKS[a] ? a : null;
}

/** @returns {string[]} */
export function trackNames() {
  return Object.keys(TRACKS);
}

/** @private Build the live node chain and phrase for a track. */
function makeTrack(id) {
  const cfg = TRACKS[id];
  const ctx = context();
  const rng = makeRng(hashSeed(`carnival:${id}`));
  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(state.masterOut);
  return {
    id,
    cfg,
    key: cfg.key,
    phrase: buildPhrase(rng, cfg.key, cfg.meter),
    gain,
    bar: 0,
    nextBarTime: 0,
    stopping: false,
  };
}

/** @private Make sure the persistent output node exists. */
function ensureOutput() {
  const ctx = context();
  if (!ctx) return false;
  if (!state.masterOut) {
    state.masterOut = ctx.createGain();
    state.masterOut.gain.value = 1;
    state.masterOut.connect(busNode('music'));
    if (state.distantTap) state.masterOut.connect(state.distantTap);
  }
  return true;
}

/**
 * Start a track, crossfading out whatever is playing.
 * @param {string} trackId
 * @param {{ fadeIn?: number, loop?: boolean }} [opts]
 */
export function play(trackId, opts = {}) {
  const id = resolveTrack(trackId) || 'hub-day';
  logPlay('@music', { track: id, ...opts });
  if (LOG_ONLY) { state.pending = id; return; }

  state.pending = id;
  whenUnlocked(() => startResolved(id, opts));
}

/** @private */
function startResolved(id, opts = {}) {
  try {
    if (!ensureOutput()) return;
    const ctx = context();
    if (state.current && state.current.id === id && !state.current.stopping) return;

    // A one-shot jingle remembers what to fall back to.
    if (!TRACKS[id].loop && TRACKS[id].bars && state.current && TRACKS[state.current.id].loop !== false) {
      state.resumeId = state.current.id;
    }
    if (state.current) fadeOut(state.current, 0.8);

    const trk = makeTrack(id);
    trk.nextBarTime = ctx.currentTime + 0.08;
    const fade = opts.fadeIn != null ? opts.fadeIn : 1.2;
    trk.gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    trk.gain.gain.linearRampToValueAtTime(1, ctx.currentTime + fade);
    state.current = trk;
    installTick();
  } catch (err) {
    reportError(err);
  }
}

/** @private Fade a track out and drop it. */
function fadeOut(trk, seconds) {
  const ctx = context();
  if (!ctx || !trk || trk.stopping) return;
  trk.stopping = true;
  const t = ctx.currentTime;
  trk.gain.gain.cancelScheduledValues(t);
  trk.gain.gain.setValueAtTime(Math.max(0.0001, trk.gain.gain.value), t);
  trk.gain.gain.linearRampToValueAtTime(0, t + seconds);
  setTimeout(() => { try { trk.gain.disconnect(); } catch { /* gone */ } }, seconds * 1000 + 400);
}

/**
 * Stop the music.
 * @param {number} [fadeSeconds]
 */
export function stop(fadeSeconds = 1) {
  logPlay('@music-stop', { fade: fadeSeconds });
  state.pending = null;
  if (LOG_ONLY) return;
  if (state.current) {
    fadeOut(state.current, Math.max(0.05, fadeSeconds));
    state.current = null;
  }
}

/** @private Schedule bars into the lookahead window. */
function tick(now) {
  const trk = state.current;
  if (!trk || trk.stopping) return;
  const ctx = context();
  if (!ctx) return;

  const barDur = (60 / trk.cfg.bpm) * trk.cfg.meter;
  let guard = 0;
  while (trk.nextBarTime < now + LOOKAHEAD && guard++ < 8) {
    const maxBars = trk.cfg.bars || 0;
    if (maxBars && trk.bar >= maxBars) {
      // A finished jingle hands the stage back to whatever it interrupted.
      const back = state.resumeId;
      state.resumeId = null;
      fadeOut(trk, 0.6);
      state.current = null;
      if (back) setTimeout(() => startResolved(back, { fadeIn: 1.5 }), 400);
      return;
    }
    if (trk.cfg.style === 'lullaby') scheduleLullaby(ctx, trk.gain, trk, trk.bar, trk.nextBarTime);
    else scheduleWaltz(ctx, trk.gain, trk, trk.bar, trk.nextBarTime);
    trk.bar += 1;
    trk.nextBarTime += barDur;
  }
}

/** @private */
function installTick() {
  if (state.offTick) return;
  state.offTick = onTick(tick);
}

/**
 * Let the ambient bed tap the music output for the "distant midway" layer.
 * @param {AudioNode|null} node
 */
export function setDistantTap(node) {
  state.distantTap = node || null;
  if (state.masterOut && node) {
    try { state.masterOut.connect(node); } catch (err) { reportError(err); }
  }
}

/** @returns {{ track: string|null, bar: number, playing: boolean }} */
export function snapshot() {
  return {
    track: state.current ? state.current.id : (LOG_ONLY ? state.pending : null),
    pending: state.pending,
    bar: state.current ? state.current.bar : 0,
    playing: !!state.current,
    tracks: trackNames(),
  };
}

/** Tear the transport down. Tests only. */
export function dispose() {
  if (state.offTick) { state.offTick(); state.offTick = null; }
  state.current = null;
  state.pending = null;
  state.masterOut = null;
  state.distantTap = null;
}
